import { DieEventTypes, isAnimationsDisabled, isDebug } from "@/constants";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import type { CurrentEvent, RandomTrigger, Trigger } from "@/types";
import { invalidate, useFrame } from "@react-three/fiber";
import { useCallback, useEffect, useRef, type RefObject } from "react";
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  Object3D,
  QuaternionKeyframeTrack,
} from "three";

// We need to clone clip adjustments otherwise they persist across different die instances
// I'm not sure at what level, but it might be cached within the `animations` of `useGltf`.
const applyClipOverrides = (clip: AnimationClip, upsideValue?: number) => {
  // Mythic
  // For a d20 max roll, we stop the Cleanup animation via an override
  // to prevent BlackHole scaling issues, but here we set up the Face20
  // to still correctly orient to face up
  if (clip.name === "BlackHole" && upsideValue === 20) {
    const clonedClip = clip.clone();
    clonedClip.tracks = [
      ...clonedClip.tracks.filter(
        (track) => track.name !== "Face20.quaternion",
      ),
      new QuaternionKeyframeTrack(
        "Face20.quaternion",
        [0],
        [
          // Taken from the last keyframe of the Cleanup Face20.quaternion track
          -0.6413267850875854, -0.04916156455874443, 0.30089637637138367,
          0.7040912508964539,
        ],
      ),
    ];

    return clonedClip;
  }

  return clip;
};

const matchesStop = (triggerStop: Trigger["triggerStop"], eventType: string) =>
  Array.isArray(triggerStop)
    ? triggerStop.includes(eventType)
    : triggerStop === eventType;

const matchesEvent = (animation: Trigger, eventType: string) =>
  animation.triggerStart === eventType ||
  matchesStop(animation.triggerStop, eventType);

export const useAnimationEventHandler = (
  dieId: string,
  clips: AnimationClip[] | undefined,
  root: RefObject<Object3D>,
  maxUpsideValue: number,
  upsideValue?: number,
  manifestAnimations?: (Trigger | RandomTrigger)[],
  preview?: boolean,
) => {
  const { setCurrentEvent, onDiceEvent, offDiceEvent } = useRenderedDiceStore();
  const { lastStandardEvent } = useRenderedDiceStore(
    (state) => state.renderedDice.entities[dieId],
  );
  const currentEventRef = useRef<CurrentEvent["trigger"] | undefined>(
    undefined,
  );
  const mixerRef = useRef<AnimationMixer | undefined>(undefined);
  const actionsRef = useRef<Record<string, AnimationAction> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (isAnimationsDisabled) return;

    const rootObject = root.current;
    if (!(rootObject && clips)) {
      actionsRef.current = undefined;
      mixerRef.current = undefined;
      return;
    }

    const mixer = new AnimationMixer(rootObject);
    const actions = clips.reduce<Record<string, AnimationAction>>(
      (acc, clip) => {
        const clipWithOverrides = applyClipOverrides(clip, upsideValue);
        const action = mixer.clipAction(clipWithOverrides, rootObject);

        if (action) {
          acc[clip.name] = action;
        }

        return acc;
      },
      {},
    );

    mixerRef.current = mixer;
    actionsRef.current = actions;

    return () => {
      Object.values(actions).forEach((action) => action.stop());
      mixer.stopAllAction();
      mixer.uncacheRoot(rootObject);

      if (mixerRef.current === mixer) {
        mixerRef.current = undefined;
      }
      actionsRef.current = undefined;
    };
  }, [clips, root, upsideValue]);

  const mixer = mixerRef.current;

  const runActionsForEvent = useCallback(
    (event?: string) => {
      const actions = actionsRef.current;
      if (!actions) return;
      // There are no animations for collisions
      if (event === DieEventTypes.COLLISION) {
        return;
      }

      // Only proceed to play animations if event type changes (unless preview)
      if (!preview && event === currentEventRef.current) {
        return;
      }

      currentEventRef.current = event;

      const isActionsEmpty = Object.keys(actions).length === 0;

      // If we lost the event type, stop everything currently playing.
      if (event === undefined) {
        if (preview && !isActionsEmpty) {
          Object.values(actions).forEach((a) => a.stop());
        }

        return;
      }

      if (!isActionsEmpty) {
        const nonCritNonFailDieRemovedAnimations: Trigger[] = [];
        let shouldNonCritNonFailDieTrigger = true;

        // Gather the potential animations to play
        // with resolving any random triggers
        const animations = manifestAnimations?.reduce<Trigger[]>(
          (acc, trigger) => {
            if (Array.isArray(trigger)) {
              const randomTrigger = trigger as RandomTrigger;
              const randomIndex = Math.floor(
                Math.random() * randomTrigger.length,
              );
              const randomSelection = randomTrigger[randomIndex];
              acc.push(
                ...(Array.isArray(randomSelection)
                  ? randomSelection
                  : [randomSelection]),
              );
            } else {
              acc.push(trigger);
            }

            return acc;
          },
          [],
        );

        // Filter the animations based on the current event and roll value
        const actionArr =
          animations?.filter((anim) => {
            let shouldUseValueAnimation = false;
            const isTriggerMatch = matchesEvent(anim, event);

            if (
              isTriggerMatch &&
              anim.value !== undefined &&
              !shouldUseValueAnimation
            ) {
              shouldUseValueAnimation = true;
            }

            if (isTriggerMatch && anim.value === upsideValue) {
              return anim;
            }

            if (!shouldUseValueAnimation && isTriggerMatch) {
              return anim;
            }

            if (event === DieEventTypes.DIE_REMOVED) {
              if (
                (upsideValue === maxUpsideValue &&
                  anim.triggerStart === "critDieRemoved") ||
                (upsideValue === 1 && anim.triggerStart === "failDieRemoved")
              ) {
                shouldNonCritNonFailDieTrigger = false;
                return anim;
              } else if (anim.triggerStart === "nonCritNonFailDieRemoved") {
                nonCritNonFailDieRemovedAnimations.push(anim);
                return;
              }
            }
          }) || [];

        if (
          shouldNonCritNonFailDieTrigger &&
          nonCritNonFailDieRemovedAnimations.length > 0
        ) {
          actionArr.push(...nonCritNonFailDieRemovedAnimations);
        }

        if ((isDebug || preview) && actionArr.length > 0) {
          console.log(
            "manifest animations triggered: ",
            actionArr.map(({ definition, triggerStart, triggerStop }) => ({
              definition,
              triggerStart,
              triggerStop: String(triggerStop),
            })),
          );
        }

        actionArr.forEach(({ definition, loop, triggerStop }) => {
          const action = actions[definition];

          if (!action) {
            if (isDebug) {
              console.warn("Missing action for animation:", definition);
            }

            return;
          }

          if (isDebug) {
            console.log("Found action for animation:", definition);
          }

          if (matchesStop(triggerStop, event)) {
            /**
             * Stopping an animation in threejs basically resets
             * the animated properties to their original state or back to a weight of 0.
             * So some sets may need to be overridden via the manifest to remove a triggerStop
             * if the behavior is really more of a pause. Elder Heart flowers is an example of this.
             */
            action.stop();
          } else {
            action.clampWhenFinished = true;
            action
              .reset()
              .setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1)
              .play();

            setCurrentEvent(dieId, {
              trigger: definition,
            });
          }
        });
      }
    },
    [
      dieId,
      manifestAnimations,
      maxUpsideValue,
      preview,
      setCurrentEvent,
      upsideValue,
    ],
  );

  useEffect(() => {
    if (isAnimationsDisabled || !onDiceEvent) return;

    const handleEvent = ({ id, trigger }: { id: string; trigger?: string }) => {
      if (id !== dieId) return;

      runActionsForEvent(trigger);
    };

    onDiceEvent("event", handleEvent);

    return () => {
      if (offDiceEvent) {
        offDiceEvent("event", handleEvent);
      }
    };
  }, [dieId, offDiceEvent, onDiceEvent, runActionsForEvent]);

  useFrame((_, delta) => {
    if (isAnimationsDisabled || !mixer || lastStandardEvent === undefined)
      return;

    mixer.update(delta);
    invalidate();
  });

  return Boolean(root.current && clips);
};
